// chat/actions.ts — the chat control executor for Lockbox (cloned from Oriora Expense's src/chat/actions.ts).
// The client router (chat/brain.ts) maps ONE user message → ONE ChatAction; this file EXECUTES that
// action against the on-device store (src/store.ts). Everything here is LOCAL SQLite over Tauri IPC —
// no network, no billing. The ONE AI-billed step (ask the documents) happens in the router via the
// EXISTING live endpoint (/api/apps/oriora_lockbox/ask), never here — the executor only renders it.
//
// Actions are derived from THIS app's own store: it has add_document + delete_document (no update →
// NO edit action), local FTS search, an on-device key-date ledger (deadlines), and the grounded ask.
//
// HITL discipline: the destructive action (delete_document) is NEVER executed from a chat turn on its
// own — the Chat UI holds it and requires an explicit Confirm tap first.

import { store, LABEL_TEXT, fmtDate, relativeDateCopy, todayLocal, type DocKind } from "../store";
import { exportBackup, importBackup } from "../backup";
import { invoke } from "../tauri-web";
import { saveCopyNow, importFromFile } from "../localFolder";
const LOCAL_SLUG = "lockbox";
import { cloudBackupSave, cloudBackupLoad, cloudBackupStatus, cloudStoreSave, BrainError } from "../brain";
import { addInstruction } from "./brain";
import type { AskCitation } from "../brain";
import { libraryAdd, libraryList, libraryRemove, libraryMove, libraryMerge } from "../library";

// A compact result row rendered inline in a chat bubble (search matches / deadlines / citations).
export interface ResultRow {
  id: string;
  primary: string;
  secondary?: string;
  trailing?: string;
}

export type ChatAction =
  | { kind: "none"; message?: string }
  | { kind: "add_document"; title: string; docKind: DocKind; body: string }
  | { kind: "delete_document"; id: string; label?: string } // UI confirms before this reaches executeAction
  | { kind: "search"; query: string }
  | { kind: "deadlines" }
  | { kind: "ask"; answer: string; citations: AskCitation[]; confidence: number }
  | { kind: "backup_save" }
  | { kind: "backup_status" }
  | { kind: "backup_restore"; provider?: "github" | "gdrive" } // UI confirms before executeAction (replaces all data)
  | { kind: "save_local_copy" }
  | { kind: "import_file" }
  | { kind: "save_to_cloud"; contentKind: "conversation" | "note"; note?: string; destination?: "github" | "gdrive" }
  | { kind: "remember"; instruction: string }
  // ── ARTIFACT TOOLS — the customer's library (fleet-wide) ────────────────────
  | { kind: "library_save"; title: string; itemKind: "lesson" | "plan" | "note"; content: string; folder?: string }
  | { kind: "library_list"; folder?: string }
  | { kind: "library_remove"; id: string; label?: string }   // UI confirms first
  | { kind: "library_move"; id: string; folder: string; label?: string }
  | { kind: "library_merge"; ids: string[]; title: string; label?: string };  // UI confirms first

export interface ActionResult {
  ok: boolean;
  message: string;   // shown back in the chat
  changed?: boolean; // documents/key-dates changed → the main screen should refresh
  rows?: ResultRow[]; // matches to render inline
}

const trunc = (s: string, n = 120) => (s.length > n ? s.slice(0, n - 1).trimEnd() + "…" : s);

export async function executeAction(a: ChatAction, ctx?: { transcript?: string }): Promise<ActionResult> {
  try {
    switch (a.kind) {
      case "none":
        return { ok: true, message: a.message ?? "" };

      case "add_document": {
        const d = await store.addDocument({ title: a.title, kind: a.docKind, body: a.body });
        const dates = d.key_date_count > 0 ? ` · ${d.key_date_count} key date${d.key_date_count !== 1 ? "s" : ""} found` : "";
        return { ok: true, message: `Saved “${d.title}” to your Lockbox${dates}.`, changed: true };
      }

      case "delete_document":
        await store.deleteDocument(a.id);
        return { ok: true, message: `Removed ${a.label ?? "the document"} from your Lockbox.`, changed: true };

      case "search": {
        const ps = await store.searchPassages(a.query, 12);
        const rows: ResultRow[] = ps.map((p, i) => ({
          id: `${p.doc_id}-${p.page}-${i}`,
          primary: p.title,
          secondary: trunc(p.snippet),
          trailing: `p.${p.page}`,
        }));
        return {
          ok: true,
          message: ps.length
            ? `${ps.length} passage${ps.length !== 1 ? "s" : ""} mention “${a.query}”.`
            : `Nothing in your documents mentions “${a.query}”.`,
          rows,
        };
      }

      case "deadlines": {
        const ks = await store.upcomingKeyDates(todayLocal(), 50);
        const rows: ResultRow[] = ks.map((k) => ({
          id: k.id,
          primary: k.doc_title,
          secondary: `${LABEL_TEXT[k.label]} ${fmtDate(k.date)}`,
          trailing: relativeDateCopy(k.date),
        }));
        return {
          ok: true,
          message: ks.length
            ? `${ks.length} upcoming deadline${ks.length !== 1 ? "s" : ""} in your key-dates ledger.`
            : "No upcoming deadlines in your documents.",
          rows,
        };
      }

      case "ask": {
        const rows: ResultRow[] = a.citations.map((c) => ({
          id: c.doc_id,
          primary: c.title,
          secondary: c.why,
          trailing: `p.${c.page}`,
        }));
        const low = a.confidence < 0.35 ? "\n\n(Not clearly stated in your documents — double-check the source.)" : "";
        return { ok: true, message: (a.answer || "I couldn't find that in your documents.") + low, rows };
      }

      case "backup_save": {
        let provider: string;
        try {
          ({ provider } = await cloudBackupSave(JSON.parse(await exportBackup())));
        } catch (e) {
          // A backup past the standard path's size limit → the device-direct GitHub
          // save (any size, straight from this device, never through Oriora). Chat can DO this
          // one itself when GitHub is already connected in the backup menu; it can't run the
          // one-time OAuth login, so when not connected it points there by name instead.
          if (e instanceof BrainError && e.code === "too_large") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected) {
              await invoke("backup_to_github_token");
              return { ok: true, message: "Your backup is large (document scans add up), so I saved it straight from this device to your GitHub — any size works that way, and it never passes through Oriora." };
            }
            return { ok: false, message: 'This backup is too large for the standard path (document scans add up). One-time fix: open the backup menu (☁ icon, top right) → "Log in with GitHub" — after that, large backups save directly from this device, and I can run them for you from chat.' };
          }
          throw e;
        }
        const name = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "cloud";
        return { ok: true, message: "Backed up your whole collection to your " + name + " — restore it on any device, any time." };
      }

      case "backup_status": {
        // The RESTORE STORY in words — same ground truth the panel's status line shows.
        const s = await cloudBackupStatus();
        if (s.connected.length === 0) {
          const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
          if (deviceGh) {
            return { ok: true, message: "GitHub is logged in on this device (backup menu), so backups save and restore directly from here — any size, never passing through Oriora. Optionally, connecting a cloud at orioralabs.com → Settings → Connections adds the everyday path all your Oriora apps share." };
          }
          return { ok: true, message: 'Nothing is connected yet. Two options: connect GitHub or Google Drive once at orioralabs.com → Settings → Connections (all your Oriora apps back up there), or open the backup menu here (☁ icon) → "Log in with GitHub" — that one stays on this device only, and handles any size, document scans included.' };
        }
        const name = (p: string) => (p === "gdrive" ? "Google Drive" : "GitHub");
        const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
        const lines: string[] = [];
        const w = s.writes_to;
        if (w) {
          const slot = s.slots[w];
          lines.push(`Your backups write to ${name(w)} (connected in your Oriora account)` +
            (slot?.backup_exists ? ` — last backup ${when(slot.updated_at)}.` : " — no backup saved yet."));
        }
        for (const p of s.connected.filter((c) => c !== w)) {
          const slot = s.slots[p];
          lines.push(`${name(p)} is also connected but not used for new backups (${name(w!)} takes priority)` +
            (slot?.backup_exists ? ` — it still holds an older backup from ${when(slot.updated_at)}.` : "."));
        }
        const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
        if (deviceGh) {
          lines.push("GitHub is also logged in on this device, so large backups save and restore directly from here — any size, never through Oriora.");
        }
        lines.push('Say "restore my documents" and I\'ll read the backup back in — or name a source, like "restore from Drive".');
        return { ok: true, message: lines.join("\n") };
      }

      case "backup_restore": {
        const src = a.provider ? (a.provider === "gdrive" ? "Google Drive" : "GitHub") : "cloud";
        let st;
        try {
          st = await cloudBackupLoad(a.provider);
        } catch (e) {
          // Large-backup handoff: if GitHub is connected on this device, chat pulls it directly
          // itself (the user already confirmed the REPLACE before this executes); it can't run the
          // one-time OAuth login, so when not connected it points at the button by name instead.
          if (e instanceof BrainError && e.code === "unavailable") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected && (!a.provider || a.provider === "github")) {
              await invoke("restore_from_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I pulled it straight from your GitHub onto this device — your data is back.", changed: true };
            }
            return { ok: false, message: `Reading your ${src} backup through Oriora didn't work — if your backup is large (it can be, with document scans), use the backup menu (☁ icon, top right) → "Log in with GitHub", which backs up and restores any size directly on this device.` };
          }
          throw e;
        }
        if (!st.connected) return { ok: false, message: "Connect GitHub or Google Drive in your Oriora account first (Settings → Service connections)." };
        if (!st.data) return { ok: false, message: a.provider ? `No backup found in your ${src} yet.` : 'No cloud backup found yet — say "back up" first.' };
        await importBackup(JSON.stringify(st.data));
        return { ok: true, message: `Restored from your ${st.provider === "gdrive" ? "Google Drive" : "GitHub"} backup.`, changed: true };
      }

      case "save_local_copy": {
        const saved = await saveCopyNow(LOCAL_SLUG);
        return saved
          ? { ok: true, message: "Saved a copy of your data to the folder you picked — it's yours to keep or move." }
          : { ok: true, message: "Cancelled — no copy saved." };
      }

      case "import_file": {
        const json = await importFromFile();
        if (json === null) return { ok: true, message: "No file chosen — nothing imported." };
        await importBackup(json);
        return { ok: true, message: "Imported your data from the file — this device now matches that backup.", changed: true };
      }

      case "save_to_cloud": {
        // Save a single note or the whole conversation to the customer's OWN cloud (their GitHub/Drive),
        // distinct from backup_save (the whole document collection). Oriora retains nothing.
        const content = a.contentKind === "conversation" ? (ctx?.transcript ?? "") : (a.note ?? "");
        if (!content.trim()) {
          return { ok: false, message: a.contentKind === "conversation" ? "There's nothing to save yet — chat with me a bit first, then ask again." : "Tell me what to save and I'll store it." };
        }
        const { provider } = await cloudStoreSave(content, { name: a.contentKind, kind: a.contentKind, destination: a.destination });
        const where = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "your cloud";
        const what = a.contentKind === "conversation" ? "our conversation" : "your note";
        return { ok: true, message: `Saved ${what} to your ${where} — it's yours to keep.` };
      }

      case "remember": {
        addInstruction(a.instruction);
        return { ok: true, message: "Noted — from now on: " + a.instruction };
      }

      // ── ARTIFACT TOOLS (the library) — device-only, fleet-wide ──────────────
      case "library_save": {
        const _item = libraryAdd(a.title, a.itemKind, a.content, a.folder ?? "");
        return { ok: true, message: `Saved "${_item.title}" to your library${_item.folder ? ` (folder: ${_item.folder})` : ""} — on this device, yours.`, changed: true };
      }
      case "library_list": {
        const _items = libraryList(a.folder);
        if (!_items.length) return { ok: true, message: a.folder ? `Nothing in "${a.folder}" yet.` : "Your library is empty — ask me to write you something and I'll keep it here." };
        return { ok: true, message: (a.folder ? `In "${a.folder}":\n` : "Your library:\n") + _items.map((i) => `• ${i.title} — ${i.kind}${i.folder ? ` · ${i.folder}` : ""}`).join("\n") };
      }
      case "library_remove":
        return libraryRemove(a.id)
          ? { ok: true, message: `Removed ${a.label ?? "it"} from your library.`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_move":
        return libraryMove(a.id, a.folder)
          ? { ok: true, message: `Moved ${a.label ?? "it"} to "${a.folder}".`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_merge": {
        const _merged = libraryMerge(a.ids, a.title);
        return _merged
          ? { ok: true, message: `Merged into "${_merged.title}" — the originals are folded in.`, changed: true }
          : { ok: false, message: "I need at least two existing items to merge." };
      }


      default:
        return { ok: false, message: "I didn't catch that — try again?" };
    }
  } catch (e) {
    return { ok: false, message: `That didn't work: ${e instanceof Error ? e.message : String(e)}` };
  }
}
